import { Card, CardContent } from '@/components/ui/card';
import { Tag, Clock, Calendar, Phone } from 'lucide-react';

interface PriceBandDiscountRule {
  id: string;
  minDays: number;
  maxDays: number | null;
  percentOff: number;
  stackable: boolean;
  priority: number;
  activeFrom: Date | string | null;
  activeTo: Date | string | null;
  label: string | null;
}

interface PriceBand {
  id: string;
  minDays: number;
  maxDays: number | null;
  label: string;
  price: number | null;
  unit: 'PER_DAY' | 'PER_BOOKING';
}

interface PriceBandScheme {
  id: string;
  name: string;
  bands?: PriceBand[];
  discounts: PriceBandDiscountRule[];
}

interface EquipmentSpecialDiscountsProps {
  priceBandScheme?: PriceBandScheme | null;
}

export function EquipmentSpecialDiscounts({ priceBandScheme }: EquipmentSpecialDiscountsProps) {
  // Debug logging
  console.log('EquipmentSpecialDiscounts received priceBandScheme:', priceBandScheme);
  console.log('Available discounts:', priceBandScheme?.discounts);
  
  // Quick test: show all discounts regardless of date to see if data is there
  if (priceBandScheme?.discounts && priceBandScheme.discounts.length > 0) {
    console.log('Found discounts, checking each one...');
    priceBandScheme.discounts.forEach((discount, index) => {
      console.log(`Discount ${index}:`, {
        id: discount.id,
        label: discount.label,
        percentOff: discount.percentOff,
        minDays: discount.minDays,
        maxDays: discount.maxDays,
        activeFrom: discount.activeFrom,
        activeTo: discount.activeTo,
        activeFromType: typeof discount.activeFrom,
        activeToType: typeof discount.activeTo
      });
    });
  }

  // Get active discounts (check date ranges)
  const activeDiscounts = priceBandScheme?.discounts?.filter(discount => {
    const now = new Date();
    
    // Handle date strings from server serialization
    let isActiveFrom = true;
    let isActiveTo = true;
    
    if (discount.activeFrom) {
      const activeFromDate = typeof discount.activeFrom === 'string' 
        ? new Date(discount.activeFrom) 
        : discount.activeFrom;
      isActiveFrom = activeFromDate <= now;
    }
    
    if (discount.activeTo) {
      const activeToDate = typeof discount.activeTo === 'string' 
        ? new Date(discount.activeTo) 
        : discount.activeTo;
      isActiveTo = activeToDate >= now;
    }
    
    const isActive = isActiveFrom && isActiveTo;
    
    console.log(`Discount ${discount.id} (${discount.label || 'No label'}):`, {
      percentOff: discount.percentOff,
      minDays: discount.minDays,
      maxDays: discount.maxDays,
      activeFrom: discount.activeFrom,
      activeTo: discount.activeTo,
      isActiveFrom,
      isActiveTo,
      isActive,
      now: now.toISOString()
    });
    
    return isActive;
  }).sort((a, b) => b.priority - a.priority) || [];

  console.log('Active discounts:', activeDiscounts);

  // TEMPORARY: Show all discounts regardless of date to debug
  const discountsToShow = priceBandScheme?.discounts || [];
  
  console.log('Discounts to show:', discountsToShow);
  
  // Don't render anything if no discounts exist
  if (discountsToShow.length === 0) {
    console.log('No discounts found at all');
    return null;
  }

  // Format days range for display
  const formatDaysRange = (minDays: number, maxDays: number | null) => {
    if (maxDays === null) {
      return `${minDays}+ Days`;
    }
    if (minDays === maxDays) {
      return `${minDays} Day${minDays > 1 ? 's' : ''}`;
    }
    return `${minDays}–${maxDays} Days`;
  };

  // Format validity period
  const formatValidityPeriod = (activeFrom: Date | string | null, activeTo: Date | string | null) => {
    if (!activeFrom && !activeTo) {
      return 'Ongoing';
    }
    if (activeFrom && activeTo) {
      return `Until ${new Date(activeTo).toLocaleDateString()}`;
    }
    if (activeTo) {
      return `Until ${new Date(activeTo).toLocaleDateString()}`;
    }
    if (activeFrom) {
      return `From ${new Date(activeFrom).toLocaleDateString()}`;
    }
    return 'Ongoing';
  };

  return (
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-8">
      <CardContent className="pt-6 p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-2">
            <Tag className="w-5 h-5 text-[var(--color-rr-green)] mr-2" />
            <h2 className="text-xl font-bold text-black">Special Rental Discounts</h2>
            <Tag className="w-5 h-5 text-[var(--color-rr-green)] ml-2" />
          </div>
          <p className="text-sm text-gray-600">
            Save more with longer rental periods
          </p>
        </div>

        {/* Discounts Grid */}
        <div className={`grid grid-cols-1 ${discountsToShow.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
          {discountsToShow.map((discount, index) => (
            <div
              key={discount.id}
              className="bg-white border-2 border-[var(--color-rr-green)] rounded-xl p-4 md:p-5 text-center relative hover:shadow-lg transition-all duration-300"
            >
              {/* Discount Badge */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[var(--color-rr-green)] text-white px-3 md:px-4 py-1 md:py-2 rounded-full text-xs md:text-sm font-bold shadow-lg">
                {discount.percentOff}% OFF
              </div>

              {/* Icon */}
              <div className="flex justify-center mt-3 md:mt-4 mb-2 md:mb-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[var(--color-rr-light-green)] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-base md:text-lg font-bold text-[var(--color-rr-green)] mb-1 md:mb-2">
                {formatDaysRange(discount.minDays, discount.maxDays)}
              </h3>

              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
                {discount.label || 'Save on extended rentals'}
              </p>

              {/* Stackable indicator */}
              {discount.stackable && (
                <div className="text-xs text-[var(--color-rr-green)] mb-2">
                  ✓ Combines with other offers
                </div>
              )}

              {/* Valid Until */}
              <div className="flex items-center justify-center text-xs text-gray-500">
                <Clock className="w-3 h-3 mr-1" />
                <span>{formatValidityPeriod(discount.activeFrom, discount.activeTo)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-4">
            💰 Discounts automatically applied to qualifying rentals
          </p>
          <a
            href="tel:0477746329"
            className="inline-flex items-center justify-center bg-[var(--color-rr-green)] hover:bg-[var(--color-rr-forest-green)] text-white font-medium py-2 px-6 rounded-lg transition-all duration-300 hover:shadow-lg space-x-2"
          >
            <Phone className="w-4 h-4" />
            <span>Call for Best Rates: 04 77 746 329</span>
          </a>
        </div>
      </CardContent>
    </Card>
  );
}